import React, { useEffect, useState } from 'react';
import "../../assets/style/eegisterProvider.css";
import { Button, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ApiCall from '../../utils/Api';
import { toast } from 'react-toastify';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { CircularProgress } from '@mui/material';
import { convertDateFormat, extractTimeFromTimestamp } from "../../utils/services";

const FundDataUnitTransactions = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [transactions, setTransactions] = useState([]);
  const [fundName, setFundName] = useState('');
  const [loading, setLoading] = useState(true);
  const [ethPrice, setEthPrice] = useState(2000);

  useEffect(() => {
    // Fetch ETH price
    const fetchEthPrice = async () => {
      try {
        const response = await ApiCall.Api.get('/thirdPartyApi/getCryptoData');
        const ethereumData = response.data.data['1027'];
        const usdPrice = ethereumData.quote.USD.price;
        setEthPrice(usdPrice);
      } catch (error) {
        console.error('Error fetching ETH price:', error);
      }
    };
    fetchEthPrice();
  }, []);

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch buyer data to get all purchases
        const response = await ApiCall.SECURE_API.get(`/buyer/getAllBuyers`);
        
        if (response.data.success && response.data.data) {
          // Filter by data unit ID
          const filteredPurchases = response.data.data.filter(purchase => {
            const dataUnitId = purchase?.dataUnit?._id || purchase?.dataUnit || purchase?.dataUnitId;
            return String(dataUnitId) === String(id);
          });

          // Get fund/data unit name
          if (filteredPurchases.length > 0) {
            const firstPurchase = filteredPurchases[0];
            const fundUnitName = firstPurchase?.fundUnitName || firstPurchase?.dataUnit?.fundUnitName || firstPurchase?.fundName || firstPurchase?.dataUnit?.name || 'Fund';
            const institutionName = firstPurchase?.institutionName || firstPurchase?.dataUnit?.provider?.institutionName || fundUnitName;
            setFundName(institutionName);
          }

          // Process each purchase transaction
          const transactionList = filteredPurchases.map((purchase, index) => {
            const selectedLevel = purchase?.selectedLevel || {};
            const ticketUSD = selectedLevel?.ticketUSD || purchase?.dataUnit?.pricingLevels?.[0]?.ticketUSD || 0;
            const ticketETH = selectedLevel?.ticketETH || purchase?.dataUnit?.pricingLevels?.[0]?.ticketETH || 0;
            
            // Calculate ticket ETH if not available
            let calculatedTicketETH = ticketETH;
            if (!calculatedTicketETH && ticketUSD && ethPrice) {
              calculatedTicketETH = ticketUSD / ethPrice;
            } else if (purchase?.priceWei) {
              // Convert from wei to ETH
              calculatedTicketETH = Number(purchase.priceWei) / 1e18;
            }
            
            const ticketsPurchased = purchase?.ticketsPurchased || 1;
            const purchaseDate = purchase?.purchaseDate || purchase?.createdAt;
            
            // Get institution details from provider
            const institutionName = purchase?.institutionName || purchase?.dataUnit?.provider?.institutionName || 'N/A';
            const institutionCode = purchase?.institutionCode || purchase?.dataUnit?.provider?.institutionCode || 'N/A';
            const instrument = purchase?.dataUnit?.instrument || 'Fund';
            const fundAndLevel = `${purchase?.fundUnitName || purchase?.fundName || 'N/A'} - ${purchase?.dataUnit?.name || 'N/A'}`;
            
            return {
              purchaseDate,
              ticketsPurchased,
              purchasePriceETH: calculatedTicketETH * ticketsPurchased,
              purchasePriceUSD: ticketUSD * ticketsPurchased,
              institutionName,
              institutionCode,
              instrument,
              fundAndLevel,
              transactionHash: purchase?.transactionHash || ''
            };
          });

          // Sort by purchase date (most recent first)
          transactionList.sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate));
          
          setTransactions(transactionList);
        }
      } catch (error) {
        console.error("Error fetching transaction details:", error);
        toast.error("Failed to fetch transaction details");
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTransactionDetails();
    }
  }, [id, ethPrice]);

  const goBack = () => {
    navigate(-1);
  };

  const formatEth = (eth) => {
    return typeof eth === 'number' ? eth.toFixed(6) : '0.000000';
  };

  const formatUsd = (usd) => {
    return typeof usd === 'number' ? usd.toFixed(2) : '0.00';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ width: "100%", backgroundColor: "#F3F4F8", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '20px 40px',
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button 
            variant="contained" 
            onClick={goBack} 
            style={{ marginRight: "20px", backgroundColor: "black" }}
          >
            {"< Back"}
          </Button>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Fund Subscriber - {fundName} - Transactions
          </Typography>
        </div>
      </div>
      
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-around", width: "100%", padding: '40px' }}>
        <div style={{ width: "98%" }}>
          <TableContainer component={Paper} style={{ borderRadius: 9 }}>
            <Table sx={{ minWidth: 1200 }} aria-label="transaction details">
              <TableHead style={{ backgroundColor: "Black" }}>
                <TableRow>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Transaction Date</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Institution</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Code</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Instrument</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Fund & Level</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Tickets</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Price</TableCell>
                  <TableCell align="center" style={{ color: "white", fontWeight: 600 }}>Purchase on Explorer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" style={{ padding: '40px' }}>
                      <Typography variant="body1" color="textSecondary">
                        No transactions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#f0f8ff' }
                      }}
                    >
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {convertDateFormat(transaction.purchaseDate)}, {extractTimeFromTimestamp(transaction.purchaseDate)}
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {transaction.institutionName}
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {transaction.institutionCode}
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {transaction.instrument}
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {transaction.fundAndLevel}
                      </TableCell>
                      <TableCell align="center" style={{ fontSize: '14px' }}>
                        {transaction.ticketsPurchased}
                      </TableCell>
                      <TableCell align="center">
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>
                            {formatEth(transaction.purchasePriceETH)} ETH
                          </span>
                          <span style={{ color: "gray", fontSize: '13px' }}>
                            ${formatUsd(transaction.purchasePriceUSD)} USD
                          </span>
                        </div>
                      </TableCell>
                      <TableCell align="center">
                        {transaction.transactionHash ? (
                          <a
                            href={`https://sepolia.etherscan.io/tx/${transaction.transactionHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              color: '#1976d2', 
                              textDecoration: 'none',
                              fontSize: '14px',
                              fontWeight: 500,
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                          >
                            View on Explorer
                          </a>
                        ) : (
                          <span style={{ 
                            color: '#999', 
                            fontSize: '14px',
                            cursor: 'not-allowed'
                          }}>
                            Explorer link not available
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default FundDataUnitTransactions;

